import React from 'react';
import { Dimensions, Platform, StyleProp, View, ViewStyle } from 'react-native';
import { appVersion } from '@/common/device-info';
import { useDraggableHeight } from '@/hooks/useDraggableHeight';
import { useQuery } from '@/hooks/useQuery';
import { versionCompare } from '@kibt/web-utils/version';
import { NativeFixedView } from '.';

/**
 * 固定实现方案：
 * 1. Android < 10.3.40 采用 style={positoin: fixed} 进行固底，同时position: fixed不需要配合bottom来实现固底，半屏容器高度是实际就是半屏的高度
 * 2. 10.4.20 >= Android >= 10.3.40 采用 FixedView 进行固底， 存在两个Bug
 *     2.1一个bug是 全屏容器打开的半屏容器，无法实现固底，所以需要使用style={positoin: fixed} 进行固底，同时position: fixed需要配合bottom来实现固底，半屏容器高度是实际是全屏高度
 *     2.2一个bug是 部分机型固底下面有刘海屏底部缝隙
 * 3. Android >= 10.4.20 解决 全屏容器打开的半屏容器，无法实现固底 的bug，同时解决
 *
 * 4. iOS < 10.3.40 采用 吸底Tag 进行固底
 * 5. iOS >= 10.3.40 采用 FixedView 进行固底
 *
 * // 最后，发现 低版本兼容即不需要fixed也不需要吸底tag， 直接计算bottom给bottom就行
 */

export const isFixedViewSupport = (() => {
    let _isFixedViewSupport = false;
    if (Platform.OS === 'android') {
        // isFixedViewSupport = versionCompare(appVersion, '10.4.20') >= 0;
        _isFixedViewSupport = versionCompare(appVersion, '10.4.30') >= 0;
    } else if (Platform.OS === 'ios') {
        // 10.4.30 本周版本
        _isFixedViewSupport = versionCompare(appVersion, '10.4.30') >= 0;
    }
    return _isFixedViewSupport;
})();

console.log('isSupportFixedView:::', isFixedViewSupport);

/**
 * @deprecated 用组件库里的
 */
export function FixedView(props: {
    // 距离顶部距离
    top?: number;
    // 距离底部距离
    bottom?: number;
    // 根节点样式
    style?: StyleProp<ViewStyle>;
    // 根节点className
    className?: string;
    children: React.ReactNode;
    // 是否需要placeholder， android fixed布局需要
    placeholder?: boolean;
}) {
    const { placeholder = false } = props;
    const openSlideFullScreen = useQuery('openSlideFullScreen') as string;
    const { fullPageHeight, height } = useDraggableHeight({ debounceTime: 50 });
    const [childrenHeight, setChildrenHeight] = React.useState(0);
    if (isFixedViewSupport) {
        return (
            <NativeFixedView
                marginTop={props.top}
                marginBottom={props.bottom ?? 0}
                openSlideFullScreen={+openSlideFullScreen === 1}
                style={props.style}
                className={props.className}>
                {props.children}
            </NativeFixedView>
        );
    }

    if (Platform.OS === 'android' && versionCompare(appVersion, '10.3.40') === -1) {
        // 半屏容器高度就是半屏的，直接用fixed估计就行
        return (
            <>
                {/* 占位符高度跟children高度一致 */}
                {placeholder ? <View style={{ height: childrenHeight }} /> : null}
                <View
                    className={props.className}
                    style={[{ position: 'absolute', width: '100%', bottom: 0 } as any, props.style]}
                    onLayout={e => {
                        setChildrenHeight(e.nativeEvent.layout.height);
                    }}>
                    {props.children}
                </View>
            </>
        );
    }

    if (Platform.OS === 'android' || Platform.OS === 'ios') {
        const pageHeight = fullPageHeight > 0 ? fullPageHeight : Dimensions.get('window').height;
        const bottom = pageHeight - height;
        return (
            <View className={props.className} style={[{ width: '100%', bottom } as any, props.style]}>
                {props.children}
            </View>
        );
    }

    // 一码多投就不处理了
    return (
        <View className={props.className} style={[{ width: '100%', bottom: 0 } as any, props.style]}>
            {props.children}
        </View>
    );
}

/**
 * @deprecated 用组件库里的
 */
export const FixedViewV2 = React.memo(FixedView);
